/**
 * TA-603 DATES2 (loop i, rebuild of the held-out DATES item per decision 168).
 *
 * A deadline or date promise the SHOP makes in a draft is a verifier figure:
 * `extractVerifierFigures` (figures.ts) adds every token read here, so the
 * verifier holds a draft as `ungrounded_number` unless a cited fact states the
 * same day. Digit dates and durations ("October 12", "10/12", "3 to 5 days")
 * stay the base figure reader's; this reader reads only day WORDS: weekdays,
 * relative days, month names with no day number, and named occasions.
 *
 * The reader is a small clause grammar over closed lists, never a model call:
 *
 * 1. Fold the text (curly quotes, format characters, letter-hyphen-letter,
 *    a.m./p.m.) and blank double-quoted spans (quoted words are not the
 *    shop's promise).
 * 2. Tokenize once, find the day terms once, split sentences at . ! ? ; and
 *    newlines, and split clauses at , : ( ) dashes and the connectors
 *    and/but/so/which/then. A clause that is only day words and fillers
 *    ("and Tuesday", "Tuesday to Saturday") joins the clause before it.
 * 3. Governance: a clause whose subject is the customer ("you" with a
 *    non-modal verb, "your" with a customer-possessive noun, "do you", "would
 *    you") is the customer's, up to the next shop subject ("we", "I", or
 *    it/a shop noun/a name with a modal or "is"), unless that subject follows
 *    if/whether/that. "you" with an attribution verb mid-clause opens the same
 *    governance, unless a relative head or a shop noun stands before it
 *    ("everything you need", "the ring you wanted"). A lead clause ("As you
 *    said,", "Per your note,") governs the next clause too. After a customer
 *    clause, a "but" clause stays the customer's unless it has a shop subject
 *    AND a modal or commitment marker.
 * 4. A governed day is the customer's; it waits. A later clause in the same
 *    sentence (or the next sentence, when the customer's ended with "?") that
 *    has no day of its own and takes it up ("no problem", "which works", "we
 *    can do that") makes it the shop's promise. "but" or a check cue drops it.
 * 5. A free day is skipped in an hours clause, after a check cue, in a past
 *    clause with no modal; it waits after a visit verb ("stop by Thursday")
 *    until a later promise clause in the sentence; otherwise it is read when
 *    the clause has commitment shape: a modal (not in a question), a
 *    commitment marker, "by"/"before" right before it, an accept phrase right
 *    after it ("Friday works"), a self-cued term ("same day", "while you
 *    wait"), or a schedule-noun subject ("Pickup is Tuesday"). In a question
 *    only a commitment marker counts; in a clause a courtesy opener begins
 *    ("Glad you could make it in on Friday") a modal does not count.
 *
 * Linear: one fold, one tokenize, one term scan with bounded look-around, and
 * one pass over each sentence's clauses; each list lookup is a Set.
 *
 * AWAITING RATIFICATION (TA-603): every closed list below is the builder's
 * first cut, derived from the probe set (deadlines.probe.loopi.test.ts), each
 * under 25 entries. A probe a list would have to grow past 25 for is a known
 * miss in the pack, never a longer list.
 *
 * Token format (kept from TA-578): `deadline:weekday:friday`,
 * `deadline:weekday:next_tuesday`, `deadline:relative:end_of_week`,
 * `deadline:month:mid_october`, `deadline:occasion:christmas`.
 */

const set = (...xs: string[]): ReadonlySet<string> => Object.freeze(new Set(xs)) as ReadonlySet<string>;
const phrases = (...xs: string[]): readonly (readonly string[])[] => Object.freeze(xs.map((x) => Object.freeze(x.split(" "))));

// ---- vocabulary: the day words read (AWAITING RATIFICATION, TA-603) ----

/** Weekday names and their abbreviations, to the canonical day. */
const WEEKDAYS: Readonly<Record<string, string>> = Object.freeze({
  monday: "monday", tuesday: "tuesday", wednesday: "wednesday", thursday: "thursday",
  friday: "friday", saturday: "saturday", sunday: "sunday",
  mon: "monday", tue: "tuesday", tues: "tuesday", wed: "wednesday", thu: "thursday",
  thur: "thursday", thurs: "thursday", fri: "friday", sat: "saturday", sun: "sunday",
});
/** Abbreviations that are also plain words: read only after a day preceder. */
const STRICT_ABBREVIATIONS = set("mon", "wed", "sat", "sun");
/** Words that make a strict abbreviation a day ("to Sat", "on Wed"). */
const DAY_PRECEDERS = set("by", "on", "next", "this", "from", "to", "until", "till", "through", "before", "and");
/** Month names. */
const MONTHS = set("january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december");
/** One-word relative days, to their token. */
const RELATIVE_WORDS: Readonly<Record<string, string>> = Object.freeze({
  tomorrow: "tomorrow", today: "today", tonight: "tonight", overnight: "overnight", eow: "end_of_week", eom: "end_of_month",
});
/** Periods that take this/next/end of ("this week", "end of the month", "month end"). */
const PERIODS = set("week", "weekend", "month", "year");
/** Named occasions, longest first, to their token. */
const OCCASIONS: readonly (readonly [readonly string[], string])[] = Object.freeze([
  [["new", "year's", "eve"], "new_years_eve"], [["christmas", "eve"], "christmas_eve"], [["valentine's", "day"], "valentines_day"],
  [["mother's", "day"], "mothers_day"], [["father's", "day"], "fathers_day"], [["new", "year's"], "new_year"],
  [["new", "year"], "new_year"], [["the", "holidays"], "holidays"], [["christmas"], "christmas"], [["xmas"], "christmas"],
  [["easter"], "easter"], [["thanksgiving"], "thanksgiving"], [["valentine's"], "valentines_day"], [["diwali"], "diwali"],
  [["hanukkah"], "hanukkah"], [["eid"], "eid"],
].map(([w, t]) => Object.freeze([Object.freeze(w as string[]), t as string] as const)));
/** Phrases that are a promise on their own (decision 133: "overnight" is not one). */
const SELF_CUED = set("same_day", "while_you_wait");

// ---- grammar lists (AWAITING RATIFICATION, TA-603) ----

/** Shop subjects: a pronoun that, as a subject, ends a customer's governance. */
const SHOP_PRONOUNS = set("we", "i", "we'll", "we're", "we've", "we'd", "i'll", "i'm", "i've", "i'd");
/** Third-person subjects that end governance when a modal or "is" follows. */
const THIRD_SUBJECTS = set("it", "it'll", "it's", "he", "she", "they", "he'll", "she'll", "they'll");
/** Shop nouns: the piece or the team; "the ring you wanted" is not the customer's clause. */
const SHOP_NOUNS = set("ring", "piece", "stone", "stones", "band", "order", "setting", "chain", "necklace", "bracelet",
  "earrings", "watch", "pendant", "repair", "sizing", "parcel", "team", "setter", "jeweller", "engraving");
/** Modals: future or ability; negated forms (can't, won't) are not here, a refusal is not a promise. */
const MODALS = set("will", "can", "could", "should", "shall", "may", "might", "gonna");
/** Commitment markers: content words that state the piece's readiness or movement. */
const COMMIT_MARKERS = phrases("ready", "done", "finished", "back", "yours", "waiting", "at the counter", "goes out", "go out",
  "going out", "ships", "ship", "arrives", "arrive", "delivered", "have it", "pick it up", "pick up", "collect it", "grab it", "do it", "expect it",
  "due", "at the latest");
/** Delivery and handover verbs: a commitment only with a shop or carrier subject (AWAITING RATIFICATION, TA-603). */
const DELIVERY_VERBS = phrases("drops it off", "drop it off", "drops off", "delivers", "deliver", "brings it", "bring it", "hands it over",
  "hand it over", "ships it", "sends it out", "send it out", "sends it", "send it");
/** Carrier subjects for a delivery verb: "our courier drops it off Tuesday" (AWAITING RATIFICATION, TA-603). */
const COURIER_NOUNS = set("courier", "driver", "post", "postman", "carrier", "mail", "ups", "fedex", "dhl", "usps");
/** Reserve and promise verbs before a day: "holding Saturday for your fitting", "promised it for Friday" (AWAITING RATIFICATION, TA-603). */
const RESERVE_VERBS = set("holding", "reserving", "booked", "promised", "committed");
/** Schedule commitments: the shop has the day booked ("is scheduled for", "we have you down for") (AWAITING RATIFICATION, TA-603). */
const SCHEDULE_COMMITS = phrases("scheduled for", "have you down for", "pickup date is", "plan on picking it up");
/** Past-tense verbs: quiet only their own clause, and only when no modal is in it. */
const PAST_VERBS = set("was", "were", "did", "had", "came", "went", "got", "sent", "spoke", "saw", "met", "emailed", "called",
  "texted", "messaged", "received", "picked", "finished", "shipped", "delivered", "arrived", "discussed", "cleared", "returned");
/** Auxiliaries that make a following past form a participle ("will be finished"). */
const PARTICIPLE_AUX = set("be", "is", "are", "been", "being", "get", "gets", "it's", "we're", "they're", "will", "should", "can", "would");
/** Quiet words: a clause about the past ("ago", "yesterday"), when no modal is in it. */
const QUIET_WORDS = set("ago", "yesterday", "earlier", "previously");
/** Words right before a day that make it past ("last Friday", "this past Monday"). */
const PAST_PRECEDERS = set("last", "past");
/** Customer-attribution verbs: "you said", "you need", "you wanted". */
const ATTRIBUTION_VERBS = set("said", "says", "asked", "asks", "mentioned", "told", "wrote", "wanted", "want", "need", "needed",
  "requested", "hoped", "hoping", "prefer", "preferred", "like", "noted", "texted", "messaged", "suggested");
/** Customer-possessive nouns: "your wedding", "your deadline", "your message". */
const CUSTOMER_NOUNS = set("wedding", "anniversary", "proposal", "deadline", "event", "party", "birthday", "trip", "flight",
  "occasion", "celebration", "partner", "fiance", "fiancee", "wife", "husband", "sister", "mom", "message", "note", "email", "text", "request");
/** Question openers that make "you" the customer ("do you need", "would you like"). */
const CUSTOMER_QUESTION_AUX = set("do", "did", "would", "are", "were");
/** Relative heads: "everything you need" is a noun phrase, not the customer's clause. */
const RELATIVE_HEADS = set("everything", "anything", "all", "what", "whatever", "something", "exactly");
/** Words a subject is looked for after ("If you need", "Since your proposal"). */
const SKIP_LEADS = set("if", "when", "since", "as", "because", "just", "understood", "ok", "okay", "oh", "sure", "yes", "also",
  "per", "now", "once", "given");
/** Lead words whose customer clause governs the next clause too ("As you said, Friday works"). */
const CARRY_LEADS = set("as", "per");
/** Words after which a shop subject does not end governance ("you asked if it will be ready"). */
const GOVERNANCE_BRIDGES = set("if", "whether", "that");
/** Take-up phrases: the shop accepts the customer's day. */
const TAKE_UP = phrases("no problem", "not a problem", "doable", "is fine", "works", "it is", "of course", "absolutely",
  "can do", "sounds good", "is confirmed", "is booked", "will do");
/** Accept phrases right after a day: the day is agreed ("Friday works", "Monday is confirmed"). */
const ACCEPT_AFTER = phrases("works", "work", "it is", "is confirmed", "is fine", "is booked", "suits");
/** Acceptances after a subject day's modal: "Friday will be fine" agrees the day, "Saturday can be busy" does not (AWAITING RATIFICATION, TA-603). */
const SUBJECT_ACCEPTS = phrases("work", "works", "suit", "suits", "do", "be fine", "be perfect", "be good", "be great", "be okay", "be ok",
  "be no problem");
/** Hours cues: a clause about opening hours, not a deadline. */
const HOURS_CUES = set("open", "opens", "closed", "closes", "hours");
/** Range joins: "Tuesday to Saturday" is a span of days, the hours phrase's shape (AWAITING RATIFICATION, TA-603). */
const RANGE_JOINS = set("to", "till", "until", "through", "thru");
/** Check cues: the shop will find out; no promise yet. */
const CHECK_CUES = phrases("check", "checking", "look into", "find out", "confirm with", "see if", "get back", "ask");
/** Visit verbs: a day the customer may come in, a promise only if a promise clause follows. */
const VISIT_VERBS = phrases("stop by", "stop in", "come in", "come by", "come see", "swing by", "pop in", "pop by", "drop by",
  "drop in", "visit", "stopping by", "coming in");
/** Courtesy openers: a clause they open has no modal shape ("Glad you could make it in on Friday"); a marker still counts. */
const COURTESY_OPENERS = set("thanks", "thank", "glad", "nice", "lovely", "great", "pleasure", "wonderful");
/** Schedule nouns: "Pickup is Tuesday", "Delivery Tuesday to Thursday" state a day the shop keeps. */
const SCHEDULE_NOUNS = set("pickup", "collection", "delivery", "dispatch", "shipping", "shipment", "fitting");
/** Attributive nouns after a day: "Christmas collection", "Black Friday pricing", "Saturday hours". */
const ATTRIBUTIVE_NOUNS = set("collection", "sale", "sales", "pricing", "price", "prices", "hours", "special", "specials", "deal",
  "deals", "offer", "offers", "menu", "catalogue", "edition");
/** Greetings before a day: "Happy Friday", "Enjoy the weekend", "Hope Saturday goes well". */
const GREETINGS = set("happy", "merry", "enjoy", "hope", "wishing", "wish");
/** Soft words skipped looking back for a greeting ("Have a lovely weekend"). */
const GREETING_SOFT = set("a", "an", "the", "lovely", "great", "nice", "good", "wonderful", "your", "this");
/** Fillers: a clause of only day words and these joins the clause before it. */
const FILLERS = set("on", "by", "the", "this", "next", "or", "to", "till", "until", "through", "thru", "from", "and", "also", "too", "before", "either");

// ---- tokens ----

const CLAUSE_PUNCT = set(",", ":", "(", ")", "\u2013", "\u2014");
const SENTENCE_PUNCT = set(".", "!", "?", ";", "\n");
const CONNECTORS = set("and", "but", "so", "which", "then");

interface Tok {
  readonly w: string;
  readonly raw: string;
  readonly upper: boolean;
  /** Offset of the token in the folded text. */
  readonly pos: number;
}

interface Term {
  readonly start: number;
  readonly end: number;
  readonly token: string;
  readonly selfCued: boolean;
}

function fold(text: string): string {
  return text
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\p{Cf}/gu, "")
    .replace(/([A-Za-z])-(?=[A-Za-z])/g, "$1 ")
    .replace(/\b([ap])\.m\.?/gi, "$1m")
    .replace(/"[^"\n]*"/g, " ");
}

const TOKEN = /[A-Za-z]+(?:'[A-Za-z]+)?|\d+(?:[.\/:]\d+)*(?:st|nd|rd|th|am|pm)?|[.,;:!?()\n\u2013\u2014]/g;
/** A day number beside a month, digits or spelled ("May fifth", "the twenty first of June"). */
const ORDINALISH = /^(?:\d+(?:st|nd|rd|th)?|first|second|third|(?:four|fif|six|seven|eigh|nin|ten|eleven|twelf|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twentie|thirtie)th|twenty|thirty)$/;

function tokenize(text: string): Tok[] {
  const out: Tok[] = [];
  for (const m of text.matchAll(TOKEN)) out.push({ w: m[0].toLowerCase(), raw: m[0], upper: /^[A-Z]/.test(m[0]), pos: m.index ?? 0 });
  return out;
}

const at = (t: readonly Tok[], i: number): string => (i >= 0 && i < t.length ? t[i]!.w : "");

function phraseAt(t: readonly Tok[], i: number, list: readonly (readonly string[])[]): number {
  for (const p of list) {
    let ok = true;
    for (let k = 0; k < p.length && ok; k++) ok = at(t, i + k) === p[k];
    if (ok) return p.length;
  }
  return 0;
}

/** The day term starting at i (with its leading "the"/"this"/"next"), or null. */
function termAt(t: readonly Tok[], i: number): Term | null {
  const w = at(t, i);
  const mk = (len: number, token: string): Term => ({ start: i, end: i + len, token, selfCued: SELF_CUED.has(token.split(":")[2]!) });
  const R = (r: string): string => `deadline:relative:${r}`;
  // the day after tomorrow
  const dat = w === "the" ? 1 : 0;
  if (at(t, i + dat) === "day" && at(t, i + dat + 1) === "after" && at(t, i + dat + 2) === "tomorrow") return mk(dat + 3, R("day_after_tomorrow"));
  if (w === "same" && at(t, i + 1) === "day") return mk(2, R("same_day"));
  if (w === "while" && at(t, i + 1) === "you" && at(t, i + 2) === "wait") return mk(3, R("while_you_wait"));
  if (w === "a" && at(t, i + 1) === "few" && (at(t, i + 2) === "days" || at(t, i + 2) === "weeks")) return mk(3, R(`a_few_${at(t, i + 2)}`));
  // (the) end of (the|this|next) <period|month>
  const e = w === "the" && at(t, i + 1) === "end" ? 1 : 0;
  if (at(t, i + e) === "end" && at(t, i + e + 1) === "of") {
    let j = i + e + 2;
    let next = false;
    if (at(t, j) === "the" || at(t, j) === "this") j++;
    else if (at(t, j) === "next") { next = true; j++; }
    const p = at(t, j);
    if (PERIODS.has(p) && p !== "weekend") return mk(j + 1 - i, R(`end_of_${next ? "next_" : ""}${p}`));
    if (MONTHS.has(p) && !next && t[j]!.upper) return mk(j + 1 - i, `deadline:month:end_of_${p}`);
  }
  // this|next <period>, the weekend, <period> end
  if ((w === "this" || w === "next") && PERIODS.has(at(t, i + 1))) return mk(2, R(`${w}_${at(t, i + 1)}`));
  if (w === "the" && at(t, i + 1) === "weekend") return mk(2, R("this_weekend"));
  if ((w === "month" || w === "year" || w === "month's" || w === "year's") && at(t, i + 1) === "end") return mk(2, R(`end_of_${w.replace("'s", "")}`));
  const rel = RELATIVE_WORDS[w];
  if (rel !== undefined) return mk(1, R(rel));
  // occasions
  for (const [ws, tok] of OCCASIONS) {
    let ok = true;
    for (let k = 0; k < ws.length && ok; k++) ok = at(t, i + k) === ws[k];
    if (ok) return mk(ws.length, `deadline:occasion:${tok}`);
  }
  // (this|next) weekday
  const lead = w === "this" || w === "next" ? 1 : 0;
  const d = WEEKDAYS[at(t, i + lead)];
  if (d !== undefined) {
    const raw = at(t, i + lead);
    if (STRICT_ABBREVIATIONS.has(raw) && lead === 0 && !DAY_PRECEDERS.has(at(t, i - 1))) return null;
    return mk(lead + 1, `deadline:weekday:${w === "next" ? `next_${d}` : d}`);
  }
  // mid <month>, <month>; a month with a day number beside it is the base reader's digit date
  const mid = w === "mid" ? 1 : 0;
  const m = at(t, i + mid);
  if (MONTHS.has(m)) {
    const j = i + mid;
    // item 153: only a written "May" is the month; "may" and "MAY" stay the modal
    if (m === "may" && (t[j]!.raw !== "May" || ["i", "we", "you"].includes(at(t, j + 1)))) return null;
    if (m === "march" && !t[j]!.upper) return null;
    if (ORDINALISH.test(at(t, j + 1)) || ORDINALISH.test(at(t, j - 1)) || (at(t, j - 1) === "of" && ORDINALISH.test(at(t, j - 2)))) return null;
    return mk(mid + 1, `deadline:month:${mid ? `mid_${m}` : m}`);
  }
  return null;
}

/** A term the grammar should never see: past, a greeting, an attributive use. */
function skipped(t: readonly Tok[], term: Term): boolean {
  if (PAST_PRECEDERS.has(at(t, term.start - 1))) return true;
  if (ATTRIBUTIVE_NOUNS.has(at(t, term.end))) return true;
  let j = term.start - 1;
  let sawA = false;
  while (GREETING_SOFT.has(at(t, j))) { if (at(t, j) === "a") sawA = true; j--; }
  const g = at(t, j);
  return GREETINGS.has(g) || (g === "have" && sawA);
}

// ---- clauses and segments ----

interface Segment {
  readonly start: number;
  readonly end: number;
  /** The connector or punctuation the clause began after ("but", ","), "" for a sentence start or a remainder. */
  readonly conn: string;
  readonly governed: boolean;
  /** A lead clause whose customer governance carries into the next clause. */
  readonly carry: boolean;
}

function isModalAt(t: readonly Tok[], i: number): boolean {
  const w = at(t, i);
  return MODALS.has(w) || w.endsWith("'ll") || (w === "going" && at(t, i + 1) === "to");
}

/** A commitment at i: a commit marker or a schedule commitment; "due to" is a cause, not a due date. */
function commitAt(t: readonly Tok[], i: number): number {
  if (at(t, i) === "due" && at(t, i + 1) === "to") return 0;
  return phraseAt(t, i, COMMIT_MARKERS) || phraseAt(t, i, SCHEDULE_COMMITS);
}

function commitIn(t: readonly Tok[], s: number, e: number): number {
  for (let i = s; i < e; i++) if (commitAt(t, i) > 0) return i;
  return -1;
}

/** A day in a range of days: a range join and another day right after it, or right before it. */
function inRange(t: readonly Tok[], term: Term, terms: ReadonlyMap<number, Term>): boolean {
  if (RANGE_JOINS.has(at(t, term.end)) && terms.has(term.end + 1)) return true;
  if (!RANGE_JOINS.has(at(t, term.start - 1))) return false;
  for (let k = term.start - 2; k >= 0 && k >= term.start - 5; k--) {
    const prev = terms.get(k);
    if (prev) return prev.end === term.start - 1;
  }
  return false;
}

function anyIn(t: readonly Tok[], s: number, e: number, pred: (i: number) => boolean): boolean {
  for (let i = s; i < e; i++) if (pred(i)) return true;
  return false;
}

function phraseIn(t: readonly Tok[], s: number, e: number, list: readonly (readonly string[])[]): number {
  for (let i = s; i < e; i++) if (phraseAt(t, i, list) > 0) return i;
  return -1;
}

function subjectAt(t: readonly Tok[], s: number, e: number): number {
  let p = s;
  while (p < e && SKIP_LEADS.has(at(t, p))) p++;
  return p;
}

/** A shop subject at p: a shop pronoun, a third-person subject, the/our/your + shop noun, or a name. */
function shopSubjectAt(t: readonly Tok[], p: number, sentenceStart: number): boolean {
  const w = at(t, p);
  if (SHOP_PRONOUNS.has(w) || THIRD_SUBJECTS.has(w)) return true;
  if ((w === "the" || w === "our" || w === "your") && SHOP_NOUNS.has(at(t, p + 1))) return true;
  return isNameAt(t, p, sentenceStart);
}

function isNameAt(t: readonly Tok[], p: number, sentenceStart: number): boolean {
  const tok = t[p];
  if (!tok || !tok.upper || p === sentenceStart || tok.w === "i") return false;
  return !(tok.w in WEEKDAYS) && !MONTHS.has(tok.w) && /^[a-z]+$/.test(tok.w);
}

/** Where a customer's governance that began at `from` ends (exclusive), within [from, e). */
function reachEnd(t: readonly Tok[], from: number, e: number, terms: ReadonlyMap<number, Term>, sentenceStart: number): number {
  for (let r = from; r < e; r++) {
    const term = terms.get(r);
    if (term) { r = term.end - 1; continue; }
    if (GOVERNANCE_BRIDGES.has(at(t, r - 1))) continue;
    const w = at(t, r);
    if (SHOP_PRONOUNS.has(w)) return r;
    let after = -1;
    if (THIRD_SUBJECTS.has(w)) after = w.includes("'") ? r : r + 1;
    else if ((w === "the" || w === "our" || w === "your") && SHOP_NOUNS.has(at(t, r + 1))) after = r + 2;
    else if (isNameAt(t, r, sentenceStart)) after = r + 1;
    if (after >= 0 && (after === r || isModalAt(t, after) || at(t, after) === "is" || at(t, after) === "will")) return r;
  }
  return e;
}

/** Where the clause [s, e) becomes the customer's: s for a customer subject, a mid-clause lead, or -1. */
function customerFrom(t: readonly Tok[], s: number, e: number, terms: ReadonlyMap<number, Term>): number {
  const p = subjectAt(t, s, e);
  const w = at(t, p);
  const n = at(t, p + 1);
  if (w === "you" && n !== "" && !isModalAt(t, p + 1)) return s;
  if (w === "you're" || w === "you've" || w === "you'd") return s;
  if (w === "your" && (CUSTOMER_NOUNS.has(n) || CUSTOMER_NOUNS.has(at(t, p + 2)))) return s;
  if (CUSTOMER_QUESTION_AUX.has(w) && n === "you") return s;
  for (let q = p + 1; q < e; q++) {
    const term = terms.get(q);
    if (term) { q = term.end - 1; continue; }
    if (at(t, q) === "you" && ATTRIBUTION_VERBS.has(at(t, q + 1)) && !RELATIVE_HEADS.has(at(t, q - 1)) && !SHOP_NOUNS.has(at(t, q - 1))) return q;
  }
  return -1;
}

// ---- reading ----
/**
 * Every day term a text states, with no grammar. NOT a grounding source
 * (fix round 1): "we are closed Friday" names Friday but promises nothing, so
 * the verifier grounds a day only through `extractDeadlines`, save the
 * period phrases of `extractPeriodMentions` (fix round 2). A day a greeting,
 * "last" or an attributive noun claims ("Black Friday pricing") is not stated.
 */
export function extractDeadlineMentions(text: string): string[] {
  const t = tokenize(fold(text));
  const out: string[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < t.length; i++) {
    const term = termAt(t, i);
    if (!term) continue;
    if (!skipped(t, term) && !seen.has(term.token)) { seen.add(term.token); out.push(term.token); }
    i = term.end - 1;
  }
  return out;
}

/** Period phrases a cited fact grounds by naming them ("Sale this week" grounds "this week"); days, occasions and months stay strict. */
const GROUNDING_PERIODS = set("deadline:relative:this_week", "deadline:relative:this_weekend", "deadline:relative:this_month");

/** The clause bounds of a period mention: `.`, `;`, `:` or a line break (not the comma, the question or the exclamation mark). */
const PERIOD_CLAUSE_END = set(".", ";", ":", "\n");
/** Words that make a clause say when the shop is NOT working or what its hours are: such a clause promises no period. */
const PERIOD_NOT_WORKING = set("open", "opens", "opening", "hours", "closed", "closes", "closing", "closure", "closures", "no", "not", "cannot", "away", "unavailable");

/** True when the word at i is an hours or absence cue: the closed list, a "n't" contraction, or "on holiday/vacation". */
function periodCueAt(t: readonly Tok[], i: number): boolean {
  const w = at(t, i);
  if (PERIOD_NOT_WORKING.has(w) || (w.length > 3 && w.endsWith("n't"))) return true;
  return w === "on" && (at(t, i + 1) === "holiday" || at(t, i + 1) === "vacation");
}

/** A `.` or `;` written straight onto the next word ("Closed;this week") ends no clause. */
function gluedAt(folded: string, tok: Tok): boolean {
  return /[A-Za-z0-9]/.test(folded.charAt(tok.pos + 1));
}

export interface PeriodMentionOptions {
  /**
   * Decision 183 (loop k, K6): drop a mention whose clause carries an hours or
   * absence cue. The fact-grounding path only (the verifier reading cited KB
   * facts); every other reader, the edit metric's `figuresDiffer` first,
   * keeps every mention, so an owner who corrects "closed this weekend" to
   * "closed this month" is still seen changing a figure.
   */
  readonly dropNotWorking?: boolean;
}

/**
 * The period phrases a text names (this week, this weekend, this month, and
 * "the rest of this week"): the fact side's one grounding by mention (fix
 * round 2). Residual for Kabir: "Sale this week" grounds "ready this week".
 *
 * With `dropNotWorking` (decision 183, K6 and its fix round 1), a mention in a
 * clause with an hours or absence cue (open, opening, hours, closed, closing,
 * closure, "no", "not", a "n't" word, away, "on holiday/vacation",
 * unavailable) is dropped: "we are closed this week" does not ground "ready
 * this week". Clauses end at `.` `;` `:` or a line break, never at a comma.
 * A run of bound tokens is one bound. A run of two or more dots ("...") is no
 * bound, nor is a `.` or `;` glued to the next word. A clause that ends at
 * `:` or a line break (a label, a hard wrap, a list) hands its cue on, and the
 * cue chains through every such bound until a hard one ("Closed: this week",
 * "Closed:\n- Monday\n- this weekend"); a `.` or `;` in the run makes it a
 * hard bound that hands nothing ("Closed Monday.\nReady this week" grounds).
 * Fix round 2 accepts the cost that "Hours: 10 to 5\nSizing is ready this
 * week" grounds nothing (fails closed, CLAUDE.md section 4). One linear pass,
 * no backtracking.
 */
export function extractPeriodMentions(text: string, opts: PeriodMentionOptions = {}): string[] {
  if (opts.dropNotWorking !== true) return extractDeadlineMentions(text).filter((m) => GROUNDING_PERIODS.has(m));
  const folded = fold(text);
  const t = tokenize(folded);
  const found: { token: string; clause: number }[] = [];
  const cued: boolean[] = [false];
  let clause = 0;
  for (let i = 0; i < t.length; i++) {
    if (PERIOD_CLAUSE_END.has(at(t, i))) {
      let hard = false;
      let soft = false;
      let j = i;
      while (j < t.length && PERIOD_CLAUSE_END.has(at(t, j))) {
        const w = at(t, j);
        if (w === ".") {
          let k = j;
          while (k < t.length && at(t, k) === ".") k++;
          if (k - j === 1 && !gluedAt(folded, t[j]!)) hard = true;
          j = k;
          continue;
        }
        if (w === ";") hard ||= !gluedAt(folded, t[j]!);
        else soft = true;
        j++;
      }
      i = j - 1;
      if (hard || soft) {
        const carry = !hard && cued[clause] === true;
        clause++;
        cued.push(carry);
      }
      continue;
    }
    if (periodCueAt(t, i)) cued[clause] = true;
    const term = termAt(t, i);
    if (!term) continue;
    if (!skipped(t, term) && GROUNDING_PERIODS.has(term.token)) found.push({ token: term.token, clause });
    // the term's own words are never cues; none of them is a boundary
    i = term.end - 1;
  }
  const out: string[] = [];
  for (const f of found) if (!cued[f.clause] && !out.includes(f.token)) out.push(f.token);
  return out;
}


interface Pending {
  readonly term: Term;
  readonly sentence: number;
}

/**
 * The shop's date promises in `text`, as `deadline:<kind>:<value>` tokens in
 * reading order, each once. Synthetic-safe: reads, never stores.
 */
export function extractDeadlines(text: string): string[] {
  const t = tokenize(fold(text));
  const terms = new Map<number, Term>();
  for (let i = 0; i < t.length; i++) {
    const term = termAt(t, i);
    if (!term) continue;
    if (!skipped(t, term)) terms.set(i, term);
    i = term.end - 1;
  }
  const read: { at: number; token: string }[] = [];
  let carried: Pending[] = [];
  let sentence = 0;
  let s = 0;
  for (let i = 0; i <= t.length; i++) {
    if (i < t.length && !SENTENCE_PUNCT.has(at(t, i))) continue;
    const isQuestion = at(t, i) === "?";
    carried = readSentence(t, s, i, terms, isQuestion, carried, sentence, read);
    sentence++;
    s = i + 1;
  }
  read.sort((a, b) => a.at - b.at);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of read) if (!seen.has(r.token)) { seen.add(r.token); out.push(r.token); }
  return out;
}

function clausesOf(t: readonly Tok[], s: number, e: number, terms: ReadonlyMap<number, Term>): { start: number; end: number; conn: string }[] {
  const raw: { start: number; end: number; conn: string }[] = [];
  let cs = s;
  let conn = "";
  for (let i = s; i <= e; i++) {
    const term = i < e ? terms.get(i) : undefined;
    if (term) { i = term.end - 1; continue; }
    const w = at(t, i);
    if (i < e && !CLAUSE_PUNCT.has(w) && !CONNECTORS.has(w)) continue;
    if (i > cs) { raw.push({ start: cs, end: i, conn }); conn = ""; }
    // ", but" keeps "but": a connector outranks the punctuation before it
    if (i < e && (conn === "" || CONNECTORS.has(w))) conn = w;
    cs = i + 1;
  }
  // a clause of only day words and fillers joins the clause before it
  const out: { start: number; end: number; conn: string }[] = [];
  for (const c of raw) {
    const prev = out[out.length - 1];
    if (prev && isFragment(t, c.start, c.end, terms)) out[out.length - 1] = { ...prev, end: c.end };
    else out.push(c);
  }
  return out;
}

function isFragment(t: readonly Tok[], s: number, e: number, terms: ReadonlyMap<number, Term>): boolean {
  let hasTerm = false;
  for (let i = s; i < e; i++) {
    const term = terms.get(i);
    if (term) { hasTerm = true; i = term.end - 1; continue; }
    if (!FILLERS.has(at(t, i))) return false;
  }
  return hasTerm;
}

function readSentence(
  t: readonly Tok[], s: number, e: number, terms: ReadonlyMap<number, Term>, isQuestion: boolean,
  carriedIn: readonly Pending[], sentence: number, read: { at: number; token: string }[],
): Pending[] {
  if (s >= e) return [...carriedIn];
  // segments: each clause, split at a governance boundary
  const segs: Segment[] = [];
  let prevGoverned = false;
  let carry = false;
  for (const c of clausesOf(t, s, e, terms)) {
    const p = subjectAt(t, c.start, c.end);
    let from = -1;
    if (carry && !shopSubjectAt(t, p, s)) from = c.start;
    else if (c.conn === "but" && prevGoverned) {
      const shop = shopSubjectAt(t, p, s) && (anyIn(t, c.start, c.end, (i) => isModalAt(t, i)) || commitIn(t, c.start, c.end) >= 0);
      if (!shop) from = c.start;
    }
    if (from < 0) from = customerFrom(t, c.start, c.end, terms);
    if (from < 0) {
      segs.push({ start: c.start, end: c.end, conn: c.conn, governed: false, carry: false });
      prevGoverned = false;
      carry = false;
      continue;
    }
    if (from > c.start) segs.push({ start: c.start, end: from, conn: c.conn, governed: false, carry: false });
    const lead = from === c.start ? subjectAt(t, c.start, c.end) : from;
    const r = reachEnd(t, Math.min(lead + 2, c.end), c.end, terms, s);
    const hasTerm = anyIn(t, from, r, (i) => terms.has(i));
    const isCarry = r === c.end && !hasTerm && CARRY_LEADS.has(at(t, c.start));
    segs.push({ start: from, end: r, conn: from === c.start ? c.conn : "", governed: true, carry: isCarry });
    if (r < c.end) segs.push({ start: r, end: c.end, conn: "", governed: false, carry: false });
    prevGoverned = true;
    carry = isCarry;
  }

  let pending: Pending[] = [...carriedIn];
  let visits: Term[] = [];
  let lastCustomerAtEnd = false;
  for (const g of segs) {
    const own: Term[] = [];
    for (let i = g.start; i < g.end; i++) {
      const term = terms.get(i);
      if (term) { own.push(term); i = term.end - 1; }
    }
    const hasModal = anyIn(t, g.start, g.end, (i) => isModalAt(t, i));
    const p = subjectAt(t, g.start, g.end);
    // a delivery verb is a commitment when the shop, a named person or a carrier hands the piece over (fix round 2)
    const deliveryAt = phraseIn(t, g.start, g.end, DELIVERY_VERBS);
    const carrier = deliveryAt >= 0 && (shopSubjectAt(t, p, s) || COURIER_NOUNS.has(at(t, p)) ||
      ((at(t, p) === "the" || at(t, p) === "our") && COURIER_NOUNS.has(at(t, p + 1))) || (t[p]?.upper === true && deliveryAt === p + 1));
    const commitFirst = commitIn(t, g.start, g.end);
    const markerAt = commitFirst >= 0 && (!carrier || commitFirst < deliveryAt) ? commitFirst : carrier ? deliveryAt : -1;
    const checkAt = phraseIn(t, g.start, g.end, CHECK_CUES);
    if (g.conn === "but" || checkAt >= 0) { pending = []; visits = []; }
    if (g.governed) {
      for (const term of own) pending.push({ term, sentence });
      lastCustomerAtEnd = true;
      continue;
    }
    lastCustomerAtEnd = false;
    if (own.length === 0) {
      if (checkAt >= 0 || g.conn === "but") continue;
      const itThatThen = anyIn(t, g.start, g.end, (i) => at(t, i) === "it" || at(t, i) === "that" || at(t, i) === "then" || at(t, i).startsWith("it'"));
      const promise = markerAt >= 0 || (hasModal && itThatThen);
      if (pending.length > 0 && (phraseIn(t, g.start, g.end, TAKE_UP) >= 0 || (shopSubjectAt(t, p, s) && promise))) {
        for (const q of pending) read.push({ at: q.term.start, token: q.term.token });
        pending = [];
      }
      if (visits.length > 0 && promise) {
        for (const v of visits) read.push({ at: v.start, token: v.token });
        visits = [];
      }
      continue;
    }
    // an hours cue quiets only a day inside the hours phrase: the cue before the day, or the day in a range (fix round 1)
    let hoursAt = -1;
    for (let i = g.start; i < g.end && hoursAt < 0; i++) if (HOURS_CUES.has(at(t, i))) hoursAt = i;
    const past = !hasModal && anyIn(t, g.start, g.end, (i) =>
      QUIET_WORDS.has(at(t, i)) || (PAST_VERBS.has(at(t, i)) && !PARTICIPLE_AUX.has(at(t, i - 1))));
    const schedule = SCHEDULE_NOUNS.has(at(t, p)) || (at(t, p) === "the" && SCHEDULE_NOUNS.has(at(t, p + 1)));
    const courtesy = COURTESY_OPENERS.has(at(t, g.start));
    // one forward sweep: a visit verb before a day, and a modal or marker between them
    let visitEnd = -1;
    let promisedSinceVisit = false;
    let scheduleSeen = false; // a schedule noun before the day
    let reserveSeen = false; // a reserve or promise verb before the day ("fully booked" is a refusal)
    for (let i = g.start; i < g.end; i++) {
      const term = terms.get(i);
      if (!term) {
        const v = phraseAt(t, i, VISIT_VERBS);
        if (v > 0) { visitEnd = i + v; promisedSinceVisit = false; i += v - 1; continue; }
        if (isModalAt(t, i) || commitAt(t, i) > 0) promisedSinceVisit = true;
        if (SCHEDULE_NOUNS.has(at(t, i))) scheduleSeen = true;
        if (RESERVE_VERBS.has(at(t, i)) && at(t, i - 1) !== "fully") reserveSeen = true;
        continue;
      }
      i = term.end - 1;
      if (past) continue;
      if (hoursAt >= 0 && (hoursAt < term.start || inRange(t, term, terms)) && !(markerAt >= 0 && markerAt < term.start)) continue;
      if (checkAt >= 0 && checkAt < term.start) continue;
      if (visitEnd >= 0 && !promisedSinceVisit) { visits.push(term); continue; }
      const before = at(t, term.start - 1);
      // a day or occasion that is the clause subject ("Saturday can be busy") is not a modal promise (fix round 1)
      const subject = term.start === p && isModalAt(t, term.end) && phraseAt(t, term.end + 1, SUBJECT_ACCEPTS) === 0;
      // a subject day whose predicate is your/the schedule noun is the shop's day ("Saturday is your pickup day", fix round 2)
      const k = at(t, term.end) === "is" ? term.end + 1 : isModalAt(t, term.end) && at(t, term.end + 1) === "be" ? term.end + 2 : -1;
      const subjectSchedule = term.start === p && k > 0 && (at(t, k) === "your" || at(t, k) === "the") && SCHEDULE_NOUNS.has(at(t, k + 1));
      const shaped = isQuestion
        ? markerAt >= 0
        : (hasModal && !courtesy && !subject) || markerAt >= 0 || before === "by" || before === "before" || phraseAt(t, term.end, ACCEPT_AFTER) > 0 ||
          term.selfCued || (schedule && scheduleSeen) || reserveSeen || subjectSchedule;
      if (shaped) read.push({ at: term.start, token: term.token });
    }
  }
  // a customer's day asked as a question waits one sentence for the shop's take-up
  return isQuestion && lastCustomerAtEnd ? pending.filter((q) => q.sentence === sentence) : [];
}
